import { useState, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TagCheckboxList } from "./TagCheckboxList";
import type { TagGroup } from "./types";

export interface TagDropdownProps {
  /** The tag group to display */
  group: TagGroup;
  /** Currently selected tag IDs from this group */
  selectedTags: string[];
  /** Callback when a tag is toggled */
  onTagToggle: (tagId: string) => void;
  /** Whether the dropdown is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Trigger element (GroupSelectorCard) */
  trigger: React.ReactNode;
  /** Whether to show counts next to each item (default: true) */
  showItemCounts?: boolean;
}

/**
 * Popover dropdown with searchable checkbox list for tag selection.
 *
 * The list itself is `TagCheckboxList`, shared with the documents Filters
 * popover — keep behaviour changes (search, hierarchy, selection) there rather
 * than re-implementing them here.
 *
 * @example
 * ```tsx
 * <TagDropdown
 *   group={projectsGroup}
 *   selectedTags={['project-123', 'project-456']}
 *   onTagToggle={(id) => toggleFilter(id)}
 *   open={openDropdown === 'projects'}
 *   onOpenChange={(open) => setOpenDropdown(open ? 'projects' : null)}
 *   trigger={<GroupSelectorCard ... />}
 * />
 * ```
 */
export function TagDropdown({
  group,
  selectedTags,
  onTagToggle,
  open,
  onOpenChange,
  trigger,
  showItemCounts = true,
}: TagDropdownProps) {
  const [search, setSearch] = useState("");

  // Clear search when dropdown closes
  useEffect(() => {
    if (!open) {
      setSearch("");
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={onOpenChange} modal={true}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start" sideOffset={4}>
        <TagCheckboxList
          group={group}
          selectedTags={selectedTags}
          onTagToggle={onTagToggle}
          searchPlaceholder={`Search ${group.label.toLowerCase()}...`}
          emptyLabel={`No ${group.label.toLowerCase()} found.`}
          showItemCounts={showItemCounts}
          search={search}
          onSearchChange={setSearch}
        />
      </PopoverContent>
    </Popover>
  );
}
